import { type CSSProperties, type ButtonHTMLAttributes, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { type EmblaCarouselType } from 'embla-carousel';
import cn from 'clsx';
import { VisuallyHidden } from '@ev-tech/ui-core-components/VisuallyHidden';

import { type CarouselThumbButtonProps } from './Carousel.types';

import styles from './CarouselThumb.module.css';

export type CarouselThumbProps = {
  scrollSnaps?: number[];
  selectedIndex?: number;
  isVisible?: boolean;
  onDotButtonClick?: (index: number) => void;
  emblaApi?: EmblaCarouselType;
  maxThumbs?: number;
  className?: string;
  style?: CSSProperties;
  thumbButtonProps?: CarouselThumbButtonProps;
};

const getThumbButtonStyle = ({
  activeColor,
  inactiveColor,
  style,
}: Pick<CarouselThumbButtonProps, 'activeColor' | 'inactiveColor' | 'style'>): CSSProperties =>
  ({
    '--carousel-thumb-active-color': activeColor,
    '--carousel-thumb-inactive-color': inactiveColor,
    ...style,
  }) as CSSProperties;

const getCarouselThumbStyle = ({ maxThumbs, style }: { maxThumbs: number; style?: CSSProperties }): CSSProperties =>
  ({
    '--carousel-line-size': 'calc(var(--distance-static-l))',
    '--carousel-thumb-max-count': maxThumbs,
    ...style,
  }) as CSSProperties;

export const CarouselThumb = ({
  scrollSnaps = [],
  selectedIndex,
  onDotButtonClick,
  emblaApi,
  maxThumbs = 4,
  isVisible = true,
  ...props
}: CarouselThumbProps) => {
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });
  useEffect(() => {
    if (!emblaApi) return;
    emblaThumbsApi?.scrollTo(emblaApi.selectedScrollSnap());
  }, [selectedIndex, emblaThumbsApi, emblaApi]);

  const { className, style, thumbButtonProps, ...thumbProps } = props;

  if (!isVisible) return null;

  const {
    activeColor,
    className: thumbButtonClassName,
    inactiveColor,
    style: thumbButtonStyle,
    ...restThumbButtonProps
  } = thumbButtonProps ?? {};

  return (
    <div {...thumbProps} className={cn(styles.carouselThumb, className)} style={getCarouselThumbStyle({ maxThumbs, style })}>
      <div className={styles.viewport} ref={emblaThumbsRef}>
        <div className={styles.container} data-center={maxThumbs > scrollSnaps.length} data-overflowing={scrollSnaps.length > maxThumbs}>
          {scrollSnaps.length > 1 &&
            scrollSnaps.map((_, index) => (
              <div className={styles.slide} key={index}>
                <button
                  className={cn(styles.lineButton, thumbButtonClassName)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDotButtonClick?.(index);
                  }}
                  data-active={index === selectedIndex}
                  style={getThumbButtonStyle({ activeColor, inactiveColor, style: thumbButtonStyle })}
                  {...(restThumbButtonProps as ButtonHTMLAttributes<HTMLButtonElement>)}
                >
                  <VisuallyHidden>{`carousel index ${index} button`}</VisuallyHidden>
                </button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
